import { timepointLabel } from '../../api/know'
import { StudySchedulePanel } from '../../components/study/StudySchedulePanel'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Chart, registerables, type ChartConfiguration } from 'chart.js'
import { palette } from '@versports/theme'
import { admin, type AthleteTimeline, type AthleteTimelineEvent } from '../../api/admin'
import { ApiError } from '../../api/client'
import { withAlpha } from '../../components/chartTheme'

Chart.register(...registerables)

/**
 * Per-athlete longitudinal view (PRD §8.5). One line chart on a shared time
 * axis overlaying (a) questionnaire total scores at each timepoint and (b) a
 * per-session performance series (goal-rate), plus a compact event table.
 * Mirrors SubscaleChart's palette/config so it follows the design system.
 */
export function AdminAthleteTimeline() {
  const { userId } = useParams<{ userId: string }>()
  const id = Number(userId)
  const [data, setData] = useState<AthleteTimeline | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await admin.athleteTimeline(id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }, [id])

  useEffect(() => {
    if (Number.isFinite(id)) void load()
  }, [id, load])

  if (error) return <div className="banner-err">{error}</div>
  if (!data) return <p className="text-sm text-text-muted">Cargando…</p>

  const a = data.athlete
  return (
    <section>
      <p>
        <Link to={`/admin/users/${id}`} className="text-sm text-text-muted">
          ← {a.display_name || a.username}
        </Link>
      </p>
      <h1>Evolución · {a.display_name || a.username}</h1>
      <p className="text-sm text-text-muted">
        {a.email || '—'}
        {a.club_id != null && <> · club #{a.club_id}</>}
      </p>

      <StudySchedulePanel userId={id} />

      {data.events.length === 0 ? (
        <p className="mt-md text-sm text-text-muted">
          Sin cuestionarios ni sesiones todavía.
        </p>
      ) : (
        <>
          <TimelineChart events={data.events} />
          <h2>Eventos</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Tipo</th>
                  <th>Detalle</th>
                  <th className="text-right">Ver</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((ev, i) => (
                  <tr key={i}>
                    <td>
                      {ev.date
                        ? new Date(ev.date).toLocaleString('es-AR', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </td>
                    <td>
                      <span className={ev.type === 'session' ? 'tag-ok' : 'tag'}>
                        {ev.type === 'session' ? 'sesión' : 'cuestionario'}
                      </span>
                    </td>
                    <td className="text-xs text-text-muted">
                      {ev.type === 'questionnaire' ? (
                        <>
                          <strong className="text-text-primary">{ev.code}</strong> ·{' '}
                          {timepointLabel(ev.timepoint)}
                          {ev.scores.total && (
                            <>
                              {' '}· Total:{' '}
                              <strong className="text-text-primary">
                                {ev.scores.total.raw}
                              </strong>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {ev.n_trials} trials · goles {ev.goals} · atajadas {ev.saves}
                          {ev.goal_rate != null && (
                            <>
                              {' '}· goal rate{' '}
                              <strong className="text-text-primary">
                                {Math.round(ev.goal_rate * 100)}%
                              </strong>
                            </>
                          )}
                        </>
                      )}
                    </td>
                    <td className="text-right">
                      {ev.type === 'session' && (
                        <Link to={`/admin/sessions/${ev.session_id}`}>detalle</Link>
                      )}
                      {ev.type === 'questionnaire' && (
                        <Link to={`/admin/questionnaires/${ev.response_id}`}>
                          detalle
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}

/** Line chart: questionnaire total (left axis) + session goal-rate % (right
 *  axis) sharing a single category axis of chronologically-ordered dates. A
 *  category axis (not `time`) avoids pulling in a Chart.js date adapter — same
 *  scale families SubscaleChart uses. Each series aligns to the shared labels;
 *  missing points are `null` and spanned. */
function TimelineChart({ events }: { events: AthleteTimelineEvent[] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart<'line'> | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    // Events already arrive date-ascending from the backend. Build the shared
    // x labels from every dated event, then map each series onto that index.
    const dated = events.filter((e) => e.date != null)
    const labels = dated.map((e) =>
      new Date(e.date as string).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }),
    )
    const totalData = dated.map((e) =>
      e.type === 'questionnaire' && e.scores.total != null ? e.scores.total.raw : null,
    )
    const rateData = dated.map((e) =>
      e.type === 'session' && e.goal_rate != null
        ? Math.round(e.goal_rate * 100)
        : null,
    )

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Total cuestionario',
            data: totalData,
            yAxisID: 'yScore',
            borderColor: palette.primary,
            backgroundColor: withAlpha(palette.primary, 0.5),
            tension: 0.25,
            spanGaps: true,
          },
          {
            label: 'Goal rate (%)',
            data: rateData,
            yAxisID: 'yRate',
            borderColor: palette.textSecondary,
            backgroundColor: withAlpha(palette.textSecondary, 0.5),
            borderDash: [5, 4],
            tension: 0.25,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: { labels: { color: palette.textSecondary, boxWidth: 12 } },
        },
        scales: {
          x: {
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          yScore: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            title: { display: true, text: 'Puntaje', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          yRate: {
            type: 'linear',
            position: 'right',
            beginAtZero: true,
            max: 100,
            title: { display: true, text: 'Goal rate %', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { display: false },
          },
        },
      },
    }

    chartRef.current = new Chart(canvasRef.current, config)
    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [events])

  return (
    <div className="my-md" style={{ height: 300 }}>
      <canvas ref={canvasRef} />
    </div>
  )
}
